import React, { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, ClipboardList, Download, Package, RotateCcw, ShoppingCart, Eye, Check,
  Plus, Search, MapPin, X, Image as ImageIcon, Menu, Home, ChevronRight, Sparkles, FileText, Receipt, Video, FolderOpen, ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { DeliveryCard } from "../components/logistics/DeliveryInfo";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { ChatButton, ChatDrawer } from "../components/ChatDrawer";
import { formatItalianDate } from "../utils/dateIt";
import { openAuthenticatedPdf } from "../utils/pdf";
import OrderRowActions from "../components/OrderRowActions";
import { useLang } from "../context/LangContext";
import { useCompany } from "../context/CompanyContext";
import { formatMoney, currencySymbol } from "../utils/currency";
import StatementsPage from "../pages/StatementsPage";
import DocumentsPage from "../pages/DocumentsPage";
import TutorialsPage from "../pages/TutorialsPage";

const useFmt = () => {
  const { company, displayMode } = useCompany();
  return (v) => formatMoney(v, company, { displayMode });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusLabelMap = (t) => ({
  pending_agent: t.statusPendingAgent,
  pending: t.statusPending,
  confirmed: t.statusConfirmed,
  in_preparation: t.statusInPrep,
  ready: t.statusReady,
  shipped: t.statusShipped,
  delivered: t.statusDelivered,
  completed: t.statusCompleted,
  cancelled: t.statusCancelled,
});
const STATUS_COLOR = {
  pending_agent: "bg-amber-100 text-amber-800",
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  in_preparation: "bg-orange-100 text-orange-700",
  ready: "bg-cyan-100 text-cyan-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const Logo = ({ className = "h-10" }) => <img src="https://customer-assets.emergentagent.com/job_sales-catalog-hub-1/artifacts/6bi5tskl_giotto-hor-bordo-carni%20%282%29.png" alt="Giotto" className={className} />;

const Header = ({ user, logout, onChatClick }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const loc = useLocation();
  const items = [
    { path: "/customer/orders", label: t.myOrders, icon: ClipboardList },
    { path: "/customer/documents", label: "Documenti", icon: FolderOpen },
    { path: "/customer/statements", label: "Scadenziario", icon: Receipt },
    { path: "/customer/offers", label: t.offers, icon: Sparkles },
    { path: "/customer/dedicated-offers", label: "Offerte dedicate", icon: FileText },
    { path: "/customer/new-order", label: t.pAgent_newOrder, icon: ShoppingCart },
    { path: "/customer/tutorials", label: "Guide", icon: Video },
  ];
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-white border-b border-stone-200 sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Logo className="h-8 sm:h-10" />
          <div className="hidden sm:block">
            <p className="text-xs text-stone-500">{t.customer}</p>
            <p className="font-semibold text-stone-900 truncate">{user?.name}</p>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-1 overflow-x-auto">
          {items.map(it => {
            const active = loc.pathname.startsWith(it.path);
            return (
              <Link key={it.path} to={it.path} className={`group px-2.5 py-2 rounded-md text-sm font-medium whitespace-nowrap inline-flex items-center ${active ? "bg-[#8A1C2A] text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`customer-nav-${it.path.split('/').pop()}`}>
                <it.icon className="w-4 h-4 inline mr-1" />{it.label}
                <span
                  role="button"
                  title="Apri in un'altra scheda"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(it.path, "_blank"); }}
                  className={`ml-1 p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity ${active ? "hover:bg-white/20" : "hover:bg-stone-200"}`}
                  data-testid={`customer-newtab-${it.path.split('/').pop()}`}
                >
                  <ExternalLink className="w-3 h-3" />
                </span>
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-1">
          <ChatButton onClick={onChatClick} />
          <Button variant="outline" onClick={() => { logout(); navigate("/login/customer"); }} className="border-stone-300" data-testid="customer-logout-btn">
            <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t.logout}</span>
          </Button>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2 -mr-2 text-stone-700" data-testid="customer-mobile-menu">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          {items.map(it => {
            const active = loc.pathname.startsWith(it.path);
            return (
              <Link key={it.path} to={it.path} onClick={() => setOpen(false)} className={`flex items-center gap-2 px-4 py-3 text-sm ${active ? "bg-[#8A1C2A]/10 text-[#8A1C2A] font-semibold" : "text-stone-700"}`}>
                <it.icon className="w-4 h-4" />{it.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
};

const OrdersHistory = () => {
  const { t } = useLang();
  const fm = useFmt();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reorderingFrom, setReorderingFrom] = useState(null);
  const [reorderDraft, setReorderDraft] = useState(null);
  const [selectedLines, setSelectedLines] = useState({});
  const [chatOrderId, setChatOrderId] = useState(null);
  const location = useLocation();

  // Auto-open order detail from ?openOrderId=... / ?openOrderNumber=... (deep-link from chat)
  useEffect(() => {
    if (!orders.length) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("openOrderId");
    const openNum = params.get("openOrderNumber");
    if (!openId && !openNum) return;
    const found = orders.find(o => o.id === openId || o.order_number === openNum || o.order_number === openId);
    if (found) setSelected(found);
  }, [orders, location.search]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orders`);
      setOrders(r.data || []);
    } catch { toast.error(t.common_error); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadOrders(); }, []);

  const downloadPdf = (orderId, orderNumber) => {
    openAuthenticatedPdf(`/orders/${orderId}/pdf?for_customer=true`, { download: true, filename: `Ordine-${orderNumber}.pdf` });
  };
  const previewPdf = (orderId) => {
    openAuthenticatedPdf(`/orders/${orderId}/pdf?for_customer=true&inline=true`);
  };

  const startReorder = async (order, partial = false) => {
    setReorderingFrom(order);
    if (partial) {
      const init = {};
      (order.items || []).forEach(it => { init[it.product_id || it.code] = true; });
      setSelectedLines(init);
      setReorderDraft(null);
    } else {
      try {
        const r = await axios.post(`${API}/orders/${order.id}/reorder`, {});
        setReorderDraft(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); setReorderingFrom(null); }
    }
  };
  const buildPartialDraft = async () => {
    const ids = Object.keys(selectedLines).filter(k => selectedLines[k]);
    if (ids.length === 0) { toast.error("Seleziona almeno una riga"); return; }
    try {
      const r = await axios.post(`${API}/orders/${reorderingFrom.id}/reorder`, { product_ids: ids });
      setReorderDraft(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const updateDraftLineQty = (idx, newQty) => {
    const q = Math.max(0, parseFloat(newQty) || 0);
    setReorderDraft(prev => {
      if (!prev) return prev;
      const items = prev.items.map((it, i) => {
        if (i !== idx) return it;
        const line_total = +(q * (Number(it.unit_price) || 0)).toFixed(2);
        return { ...it, quantity: q, line_total };
      }).filter(it => it.quantity > 0);
      return { ...prev, items };
    });
  };
  const updateDraftLineUnit = (idx, newUnit) => {
    setReorderDraft(prev => prev ? { ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, unit: newUnit } : it) } : prev);
  };
  const confirmReorder = async () => {
    try {
      const payload = {
        customer: reorderDraft.customer,
        items: reorderDraft.items,
        customer_id: reorderDraft.customer_id,
        pricelist_type_id: reorderDraft.pricelist_type_id,
      };
      const r = await axios.post(`${API}/orders`, payload);
      toast.success(`Nuovo ordine creato: ${r.data.order_number}`);
      setReorderingFrom(null); setReorderDraft(null); setSelectedLines({});
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900 mb-1">I miei ordini</h1>
          <p className="text-stone-500 text-sm">{orders.length} ordini totali</p>
        </div>
        <Link to="/customer/new-order">
          <Button className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="customer-new-order-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo ordine
          </Button>
        </Link>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
          : orders.length === 0 ? <div className="p-8 text-center text-stone-500"><Package className="w-12 h-12 mx-auto mb-3 text-stone-300" /><p>Nessun ordine ancora — crea il tuo primo ordine!</p></div>
            : (
              <div className="divide-y divide-stone-100">
                {orders.map(o => {
                  const isShipped = ["shipped", "delivered", "completed"].includes(o.status);
                  return (
                    <div key={o.id} className="p-4 hover:bg-stone-50" data-testid={`customer-order-${o.order_number}`}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <span className="font-mono font-semibold text-[#8A1C2A] text-sm sm:text-base">{o.order_number}</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={STATUS_COLOR[o.status]}>{statusLabelMap(t)[o.status]}</Badge>
                          {o.is_fully_paid && <Badge className="bg-green-100 text-green-700">✓ Saldato</Badge>}
                          {!o.is_fully_paid && (o.paid_amount || 0) > 0 && <Badge className="bg-orange-100 text-orange-700">Parziale</Badge>}
                          {isShipped && <Badge className="bg-stone-100 text-stone-600 text-[10px]">🔒 Non modificabile</Badge>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between text-sm gap-2">
                        <div className="text-stone-600 cursor-pointer flex-1 min-w-0" onClick={() => setSelected(o)}>
                          <span>{new Date(o.created_at).toLocaleDateString('it-IT')}</span>
                          <span className="mx-2">·</span>
                          <span>{o.items?.length || 0} articoli</span>
                          {o.shipping_date && <><span className="mx-2 hidden sm:inline">·</span><br className="sm:hidden" /><span>🚚 {o.shipping_date}</span></>}
                          {o.expected_delivery_date && <><span className="mx-2 hidden sm:inline">·</span><br className="sm:hidden" /><span>📅 {o.expected_delivery_date}</span></>}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-right">
                            <strong className="text-[#8A1C2A] block">{fm(((o.final_grand_total ?? o.grand_total ?? o.total_amount) || 0))} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></strong>
                            <span className="text-xs text-stone-500">{fm(((o.final_subtotal_amount ?? o.subtotal_amount ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></span>
                          </span>
                          <OrderRowActions
                            order={o}
                            forCustomer={true}
                            onOpenDetail={(ord) => setSelected(ord)}
                            onOpenChat={(id) => setChatOrderId(id)}
                          />
                          <Button size="sm" onClick={() => startReorder(o, false)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-8" data-testid={`reorder-${o.order_number}`}>
                            <RotateCcw className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Ripeti</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => startReorder(o, true)} className="border-[#C5A059] text-[#8A1C2A] h-8" data-testid={`reorder-partial-${o.order_number}`}>
                            Parz.
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
      </Card>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="customer-order-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  <span>Ordine</span>
                  <span className="font-mono text-[#8A1C2A]">{selected.order_number}</span>
                  <Badge className={STATUS_COLOR[selected.status]}>{statusLabelMap(t)[selected.status]}</Badge>
                  {selected.is_fully_paid && <Badge className="bg-green-100 text-green-700">✓ Saldato</Badge>}
                </DialogTitle>
              </DialogHeader>
              {(selected.shipping_date || selected.expected_delivery_date || selected.delivery_address) && (
                <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
                  {selected.delivery_address && (
                    <p>📍 <strong>{selected.delivery_address.label || "Consegna"}</strong>: {selected.delivery_address.address}, {selected.delivery_address.city} {selected.delivery_address.cap}</p>
                  )}
                  {selected.delivery_address?.notes && <p className="text-xs italic text-stone-600">Note: {selected.delivery_address.notes}</p>}
                  {selected.shipping_date && <p>🚚 Spedizione: <strong>{selected.shipping_date}</strong></p>}
                  {selected.expected_delivery_date && <p>📅 Consegna prevista: <strong>{selected.expected_delivery_date}</strong></p>}
                  {selected.document_reference && <p>📄 Rif. documento: <strong>{selected.document_reference}</strong></p>}
                </Card>
              )}
              {selected.delivery_notes && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded" data-testid="customer-order-delivery-notes">
                  <p className="text-xs font-semibold text-amber-800 mb-1">📝 Note ordine</p>
                  <p className="text-sm text-stone-800 whitespace-pre-wrap">{selected.delivery_notes}</p>
                </div>
              )}
              <Card className="bg-white border-stone-200 overflow-hidden">
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-2 text-xs">Codice</th>
                      <th className="text-left p-2 text-xs">Descrizione</th>
                      <th className="text-right p-2 text-xs">Q.tà</th>
                      <th className="text-right p-2 text-xs">€/UM</th>
                      <th className="text-right p-2 text-xs">Tot.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.items?.map((it, idx) => (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="p-2 font-mono text-xs">{it.code}</td>
                        <td className="p-2">{it.description}{it.availability_status === "missing" && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">Non disponibile</Badge>}</td>
                        <td className="p-2 text-right">{it.actual_quantity ?? it.quantity} {it.unit}</td>
                        <td className="p-2 text-right">{fm(it.unit_price)}</td>
                        <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm(((it.actual_line_total ?? it.line_total) || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200 bg-stone-50">
                      <td colSpan="4" className="px-2 py-1.5 text-right text-xs">Imponibile (IVA escl.)</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fm(((selected.final_subtotal_amount ?? selected.subtotal_amount ?? selected.total_amount) || 0))}</td>
                    </tr>
                    <tr className="bg-stone-50">
                      <td colSpan="4" className="px-2 py-1 text-right text-xs">IVA</td>
                      <td className="px-2 py-1 text-right text-sm">{fm(((selected.final_vat_total ?? selected.vat_total) || 0))}</td>
                    </tr>
                    <tr className="bg-stone-50 border-t border-stone-200">
                      <td colSpan="4" className="p-2 text-right font-semibold">Totale (IVA incl.)</td>
                      <td className="p-2 text-right text-lg font-bold text-[#8A1C2A]">{fm(((selected.final_grand_total ?? selected.grand_total ?? selected.total_amount) || 0))}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100" data-testid="cust-order-items-mobile">
                  {selected.items?.map((it, idx) => (
                    <div key={idx} className={`p-3 ${it.availability_status === "missing" ? "bg-red-50" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-mono text-stone-500">{it.code}</p>
                          <p className="text-sm font-medium leading-tight">{it.description}</p>
                          {it.availability_status === "missing" && <Badge className="mt-1 bg-red-100 text-red-700 text-[10px]">Non disponibile</Badge>}
                        </div>
                        <p className="font-semibold text-[#8A1C2A] text-sm whitespace-nowrap">{fm(((it.actual_line_total ?? it.line_total) || 0))}</p>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-stone-600">
                        <span>{it.actual_quantity ?? it.quantity} {it.unit}</span>
                        <span>{fm(it.unit_price)} / {it.unit || "Kg"}</span>
                      </div>
                    </div>
                  ))}
                  <div className="p-3 bg-stone-50 text-sm space-y-1">
                    <div className="flex items-center justify-between"><span className="text-stone-600 text-xs">Imponibile (IVA escl.)</span><span className="font-semibold">{fm(((selected.final_subtotal_amount ?? selected.subtotal_amount ?? selected.total_amount) || 0))}</span></div>
                    <div className="flex items-center justify-between"><span className="text-stone-600 text-xs">IVA</span><span>{fm(((selected.final_vat_total ?? selected.vat_total) || 0))}</span></div>
                    <div className="flex items-center justify-between pt-1 border-t border-stone-200"><span className="font-semibold">Totale (IVA incl.)</span><span className="font-bold text-lg text-[#8A1C2A]">{fm(((selected.final_grand_total ?? selected.grand_total ?? selected.total_amount) || 0))}</span></div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reorderingFrom} onOpenChange={() => { setReorderingFrom(null); setReorderDraft(null); setSelectedLines({}); }}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="reorder-dialog">
          <DialogHeader>
            <DialogTitle>
              {reorderDraft ? "Conferma nuovo ordine" : `Seleziona articoli da ripetere — ${reorderingFrom?.order_number}`}
            </DialogTitle>
          </DialogHeader>
          {!reorderDraft ? (
            <>
              <p className="text-sm text-stone-600">Seleziona quali articoli vuoi ripetere. I prezzi verranno aggiornati a quelli attuali del listino.</p>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {(reorderingFrom?.items || []).map((it, idx) => {
                  const key = it.product_id || it.code;
                  return (
                    <label key={idx} className="flex items-center gap-3 p-2 border border-stone-200 rounded cursor-pointer hover:bg-stone-50" data-testid={`reorder-line-${idx}`}>
                      <input type="checkbox" checked={!!selectedLines[key]} onChange={e => setSelectedLines({ ...selectedLines, [key]: e.target.checked })} className="w-4 h-4 accent-[#8A1C2A]" />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-stone-500">{it.code}</p>
                        <p className="text-sm truncate">{it.description}</p>
                      </div>
                      <span className="text-sm text-stone-500 whitespace-nowrap">{it.quantity} {it.unit}</span>
                    </label>
                  );
                })}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReorderingFrom(null); setSelectedLines({}); }} className="border-stone-300">Annulla</Button>
                <Button onClick={buildPartialDraft} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="build-partial-btn">Avanti →</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <p className="text-sm text-stone-600">Modifica le quantità o l&apos;unità di misura. L&apos;ordine sarà inviato al tuo agente per revisione e approvazione.</p>
              <Card className="bg-white border-stone-200 overflow-hidden">
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-2 text-xs">Codice</th>
                      <th className="text-left p-2 text-xs">Descrizione</th>
                      <th className="text-right p-2 text-xs w-24">Q.tà</th>
                      <th className="text-center p-2 text-xs w-20">U.M.</th>
                      <th className="text-right p-2 text-xs w-20">€/UM</th>
                      <th className="text-right p-2 text-xs w-24">Tot.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reorderDraft.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="p-2 font-mono text-xs">{it.code}</td>
                        <td className="p-2">{it.description}</td>
                        <td className="p-2">
                          <Input
                            type="number" step="0.1" min="0"
                            value={it.quantity}
                            onChange={(e) => updateDraftLineQty(idx, e.target.value)}
                            className="h-8 text-sm text-right bg-white border-stone-300"
                            data-testid={`reorder-qty-${idx}`}
                          />
                        </td>
                        <td className="p-2">
                          <Select value={it.unit || "Kg"} onValueChange={(v) => updateDraftLineUnit(idx, v)}>
                            <SelectTrigger className="h-8 text-xs bg-white border-stone-300" data-testid={`reorder-unit-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Kg">Kg</SelectItem>
                              <SelectItem value="Pz">Pz</SelectItem>
                              <SelectItem value="CT">CT</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2 text-right">{fm(it.unit_price)}</td>
                        <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200 bg-stone-50">
                      <td colSpan="5" className="p-2 text-right font-semibold">Totale imponibile</td>
                      <td className="p-2 text-right text-lg font-bold text-[#8A1C2A]">{fm(reorderDraft.items.reduce((s, it) => s + it.line_total, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100" data-testid="cust-reorder-items-mobile">
                  {reorderDraft.items.map((it, idx) => (
                    <div key={idx} className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-mono text-stone-500">{it.code}</p>
                          <p className="text-sm font-medium leading-tight">{it.description}</p>
                        </div>
                        <p className="font-semibold text-[#8A1C2A] text-sm whitespace-nowrap">{fm(it.line_total)}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-stone-500 uppercase">Quantità</Label>
                          <Input type="number" step="0.1" min="0" value={it.quantity} onChange={(e) => updateDraftLineQty(idx, e.target.value)} className="h-8 text-sm" data-testid={`reorder-qty-mob-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-stone-500 uppercase">U.M.</Label>
                          <Select value={it.unit || "Kg"} onValueChange={(v) => updateDraftLineUnit(idx, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`reorder-unit-mob-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Kg">Kg</SelectItem>
                              <SelectItem value="Pz">Pz</SelectItem>
                              <SelectItem value="CT">CT</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-stone-500">Prezzo: {fm(it.unit_price)} / {it.unit || "Kg"}</p>
                    </div>
                  ))}
                  <div className="p-3 bg-stone-50 flex items-center justify-between">
                    <span className="font-semibold text-sm">Totale imponibile</span>
                    <span className="font-bold text-lg text-[#8A1C2A]">{fm(reorderDraft.items.reduce((s, it) => s + it.line_total, 0))}</span>
                  </div>
                </div>
              </Card>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReorderingFrom(null); setReorderDraft(null); setSelectedLines({}); }} className="border-stone-300">Annulla</Button>
                <Button onClick={confirmReorder} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="confirm-reorder-btn">
                  <Check className="w-4 h-4 mr-1" /> Conferma e invia
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Order-scoped chat (opened by row "Chat" button) */}
      <ChatDrawer open={!!chatOrderId} onClose={() => setChatOrderId(null)} initialOrderId={chatOrderId} />
    </div>
  );
};

const CustomerNewOrder = () => {
  const { t } = useLang();
  const fm = useFmt();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cart, setCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [specialRequests, setSpecialRequests] = useState("");
  const [historyOnly, setHistoryOnly] = useState(false);
  const [historySet, setHistorySet] = useState(null); // Set of product_ids previously ordered
  const [customerSelfId, setCustomerSelfId] = useState(null);
  // Delivery
  const [selectedDeliveryIdx, setSelectedDeliveryIdx] = useState("__sede__");
  const [customDeliveryAddr, setCustomDeliveryAddr] = useState({ label: "", address: "", city: "", cap: "", province: "", notes: "" });
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [showRevision, setShowRevision] = useState(false);

  // Pre-load offer from query string ?offer_id=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oid = params.get("offer_id");
    if (!oid) return;
    axios.get(`${API}/offers/${oid}`).then(r => {
      const o = r.data;
      const banner = `OFFERTA ${o.title}${o.notes ? " - " + o.notes : ""}`;
      setCart([{
        product_id: o.product_id || null,
        code: o.product_code || "OFF",
        description: banner,
        category_id: o.category_id || null,
        category_name: o.category_name || "",
        quantity: 1, unit: o.unit || "Kg",
        unit_price: o.price, line_total: o.price,
        is_free: !o.product_id, vat_rate: 0.10,
        from_offer_id: o.id,
      }]);
      toast.success(`Offerta "${o.title}" aggiunta al carrello`);
    }).catch(() => {});
  }, []);

  const loadCatalog = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (categoryFilter !== "all") params.append("category_id", categoryFilter);
      const r = await axios.get(`${API}/customer/catalog?${params}`);
      setCatalog(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento catalogo"); }
  };
  useEffect(() => { const t = setTimeout(loadCatalog, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, categoryFilter]);

  // Load history (previously-ordered product ids) once
  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`);
        const cid = me.data?.customer_id;
        if (!cid) return;
        setCustomerSelfId(cid);
        const r = await axios.get(`${API}/customers/${cid}/ordered-items`);
        const ids = new Set((r.data?.items || []).map(i => i.product_id).filter(Boolean));
        setHistorySet(ids);
      } catch (e) {
        // History opzionale: se fallisce mostriamo solo il catalogo completo senza il toggle "Già ordinati".
        if (process.env.NODE_ENV !== "production") console.debug("[CustomerPortal] history load failed:", e?.message);
      }
    })();
  }, []);

  const customer = catalog?.customer;
  const isForeign = !!customer?.is_foreign;

  const addToCart = (prod) => {
    setCart(prev => {
      const idx = prev.findIndex(p => p.product_id === prod.id);
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], quantity: c[idx].quantity + 1, line_total: +((c[idx].quantity + 1) * c[idx].unit_price).toFixed(2) };
        return c;
      }
      return [...prev, {
        product_id: prod.id, code: prod.code, description: prod.description,
        category_id: prod.category_id, category_name: prod.category_name || "",
        quantity: 1, unit: prod.unit || "Kg", unit_price: prod.price, line_total: prod.price,
        is_free: false, vat_rate: prod.vat_rate ?? 0.10,
      }];
    });
  };
  const updateLine = (idx, field, value) => {
    setCart(prev => {
      const c = [...prev];
      c[idx] = { ...c[idx], [field]: value };
      c[idx].line_total = +(c[idx].quantity * c[idx].unit_price).toFixed(2);
      return c;
    });
  };
  const removeLine = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const subtotal = cart.reduce((s, it) => s + (it.line_total || 0), 0);
  const vatTotal = isForeign ? 0 : cart.reduce((s, it) => s + (it.line_total * (it.vat_rate || 0)), 0);
  const grandTotal = subtotal + vatTotal;

  const resolveDeliveryAddress = () => {
    if (!customer) return null;
    if (selectedDeliveryIdx === "__sede__") {
      return { label: "Sede legale", address: customer.address || "", city: customer.city || "", cap: customer.cap || "", province: customer.province || "", notes: "" };
    }
    if (selectedDeliveryIdx === "__new__") return { ...customDeliveryAddr };
    const idx = parseInt(selectedDeliveryIdx, 10);
    const da = (customer.delivery_addresses || [])[idx];
    return da ? { ...da } : null;
  };

  const openRevision = () => {
    if (cart.length === 0) { toast.error("Carrello vuoto"); return; }
    if (selectedDeliveryIdx === "__new__" && !customDeliveryAddr.address) {
      toast.error("Indirizzo di consegna obbligatorio"); return;
    }
    setShowRevision(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const delivery_address = resolveDeliveryAddress();
      const payload = {
        customer: {
          customer_name: customer.business_name,
          company: customer.business_name,
          vat_number: customer.vat_number,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          city: customer.city,
          cap: customer.cap,
          province: customer.province,
          is_foreign: !!customer.is_foreign,
          special_requests: specialRequests,
        },
        items: cart,
        customer_id: customer.id,
        delivery_address,
        delivery_notes: deliveryNotes,
      };
      const r = await axios.post(`${API}/orders`, payload);
      toast.success(`Ordine ${r.data.order_number} inviato in revisione al tuo agente!`);
      setCart([]); setSpecialRequests(""); setDeliveryNotes("");
      setSelectedDeliveryIdx("__sede__"); setCustomDeliveryAddr({ label: "", address: "", city: "", cap: "", province: "", notes: "" });
      setShowRevision(false);
      navigate("/customer/orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio ordine"); }
    finally { setSubmitting(false); }
  };

  if (!catalog) return <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-6 space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-stone-900">Nuovo ordine</h1>
        <p className="text-stone-500 text-sm">Scegli i prodotti e l'indirizzo di consegna. L'ordine sarà revisionato dal tuo agente prima dell'invio al magazzino.</p>
      </div>

      <Card className="bg-amber-50 border-amber-200 p-3">
        <div className="flex items-start gap-2 text-xs">
          <ShoppingCart className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-amber-900">Dopo l'invio, l'ordine andrà in stato <strong>"In revisione agente"</strong>. Riceverai conferma in quest'area quando il tuo agente l'avrà approvato e successivamente quando il magazzino l'avrà preso in carico.</p>
        </div>
      </Card>

      {/* Delivery address selector */}
      <Card className="bg-white border-stone-200 p-4">
        <Label className="text-stone-700 flex items-center gap-1"><MapPin className="w-4 h-4 text-[#8A1C2A]" /> Indirizzo di consegna</Label>
        <Select value={selectedDeliveryIdx} onValueChange={setSelectedDeliveryIdx}>
          <SelectTrigger className="bg-white border-stone-300 mt-1" data-testid="customer-delivery-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__sede__">Sede legale — {customer.address}, {customer.city}</SelectItem>
            {(customer.delivery_addresses || []).map((d, i) => (
              <SelectItem key={i} value={String(i)}>{d.label || `Indirizzo ${i + 1}`} — {d.address}, {d.city}</SelectItem>
            ))}
            <SelectItem value="__new__">+ Aggiungi nuovo indirizzo (una tantum)</SelectItem>
          </SelectContent>
        </Select>
        {selectedDeliveryIdx === "__new__" && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2 bg-amber-50 p-3 rounded border border-amber-200" data-testid="customer-custom-delivery-form">
            <div className="md:col-span-3"><Label className="text-xs">{t.label}</Label><Input value={customDeliveryAddr.label} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, label: e.target.value })} className="bg-white border-stone-300 h-9" placeholder="Es. Cantiere" /></div>
            <div className="md:col-span-5"><Label className="text-xs">Indirizzo *</Label><Input value={customDeliveryAddr.address} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, address: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-2"><Label className="text-xs">{t.city}</Label><Input value={customDeliveryAddr.city} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, city: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-1"><Label className="text-xs">CAP</Label><Input value={customDeliveryAddr.cap} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, cap: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-1"><Label className="text-xs">Prov.</Label><Input value={customDeliveryAddr.province} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, province: e.target.value })} className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-12"><Label className="text-xs">Note indirizzo</Label><Input value={customDeliveryAddr.notes} onChange={e => setCustomDeliveryAddr({ ...customDeliveryAddr, notes: e.target.value })} className="bg-white border-stone-300 h-9" placeholder="Riferimento, orari, citofono..." /></div>
          </div>
        )}
        {(() => { const da = resolveDeliveryAddress(); return <div className="mt-3"><DeliveryCard cap={da?.cap} localita={da?.city} simulate={3} compact testid="customer-delivery-card" /></div>; })()}
        <div className="mt-3">
          <Label className="text-xs">Note di consegna (specifiche per questo ordine)</Label>
          <Input value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)} className="bg-white border-stone-300" placeholder="Es. consegnare al magazzino retro" data-testid="customer-delivery-notes" />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Catalog column */}
        <div className="lg:col-span-2">
          <Card className="bg-white border-stone-200 p-4">
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input placeholder="Cerca prodotto..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="customer-catalog-search" />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-white border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte categorie</SelectItem>
                  {catalog?.categories?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {historySet && historySet.size > 0 && (
                <Button
                  type="button"
                  variant={historyOnly ? "default" : "outline"}
                  onClick={() => setHistoryOnly(!historyOnly)}
                  className={historyOnly ? "bg-[#8A1C2A] hover:bg-[#6B1420] text-white" : "border-stone-300"}
                  data-testid="customer-history-toggle"
                >
                  {historyOnly ? "Mostra tutti" : `⭐ Già ordinati (${historySet.size})`}
                </Button>
              )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {catalog?.products?.filter(p => !historyOnly || historySet?.has(p.id)).map(p => (
                  <Card key={p.id} className={`bg-white border transition-colors overflow-hidden ${historySet?.has(p.id) ? "border-amber-300 ring-1 ring-amber-100" : "border-stone-200 hover:border-[#8A1C2A]"}`} data-testid={`customer-prod-${p.code}`}>
                    <div className="flex items-stretch gap-2">
                      <button type="button" onClick={() => setProductDetail(p)} className="shrink-0 w-20 h-20 bg-stone-100 flex items-center justify-center">
                        {p.image_path ? (
                          <img src={p.image_path.startsWith('http') ? p.image_path : `${BACKEND_URL}/api/files/${p.image_path}`} alt={p.description} className="w-full h-full object-cover" />
                        ) : (<ImageIcon className="w-7 h-7 text-stone-300" />)}
                      </button>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <div className="flex items-center gap-1">
                          <p className="font-mono text-[10px] text-stone-500">{p.code}</p>
                          {historySet?.has(p.id) && <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1 rounded">⭐ STORICO</span>}
                        </div>
                        <p className="text-sm font-medium leading-tight truncate" title={p.description}>{p.description}</p>
                        {p.category_name && <p className="text-[11px] font-bold text-[#8A1C2A] uppercase tracking-wide truncate">{p.category_name}</p>}
                        <div className="flex items-center justify-between mt-1">
                          <span className="font-semibold text-[#8A1C2A] text-sm">{p.price > 0 ? `${fm(p.price)}` : "—"}<span className="text-[10px] text-stone-500"> / {p.unit || "Kg"}</span></span>
                          <Button size="sm" onClick={() => addToCart(p)} disabled={!p.price || p.price <= 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-7" data-testid={`customer-add-${p.code}`}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {(!catalog?.products || catalog.products.length === 0) && <p className="p-4 text-center text-stone-500 text-sm">{t.pAgent_noProductFound}</p>}
            </div>
          </Card>
        </div>
        {/* Cart column */}
        <Card className="bg-white border-stone-200 p-4 lg:sticky lg:top-20 lg:self-start">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-stone-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-[#8A1C2A]" /> Carrello</h3>
            <span className="text-xs text-stone-500">{cart.length} articoli</span>
          </div>
          {cart.length === 0 ? (
            <p className="py-8 text-center text-stone-400 text-sm">Vuoto. Aggiungi prodotti dal catalogo.</p>
          ) : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {cart.map((it, idx) => (
                <div key={idx} className="p-2 border border-stone-200 rounded" data-testid={`customer-cart-line-${idx}`}>
                  <p className="text-xs text-stone-500 font-mono">{it.code}</p>
                  <p className="text-sm font-medium truncate">{it.description}</p>
                  <div className="grid grid-cols-12 gap-1 items-center mt-1">
                    <Input type="number" step="0.1" min="0" value={it.quantity} onChange={e => updateLine(idx, "quantity", parseFloat(e.target.value) || 0)} className="col-span-3 h-8 text-sm text-right bg-white border-stone-300" data-testid={`customer-cart-qty-${idx}`} />
                    <Select value={it.unit} onValueChange={(v) => updateLine(idx, "unit", v)}>
                      <SelectTrigger className="col-span-3 h-8 text-xs bg-white border-stone-300" data-testid={`customer-cart-unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="Pz">Pz</SelectItem>
                        <SelectItem value="CT">CT</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="col-span-5 text-sm text-right font-semibold text-[#8A1C2A]">{fm((it.line_total || 0))}</span>
                    <Button size="icon" variant="ghost" className="col-span-1 h-7 w-7" onClick={() => removeLine(idx)}><X className="w-3 h-3 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-stone-200 mt-3 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-stone-600">Imponibile</span><span className="font-semibold">{fm(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">IVA {isForeign ? "(0%)" : ""}</span><span className="font-semibold">{fm(vatTotal)}</span></div>
            <div className="flex justify-between text-lg pt-1 border-t border-stone-200"><span className="font-semibold">Totale</span><span className="font-bold text-[#8A1C2A]">{fm(grandTotal)}</span></div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">{t.pAgent_specialRequests}</Label>
            <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)} rows={2} placeholder="Note per il tuo agente..." className="w-full mt-1 px-3 py-2 text-xs bg-white border border-stone-300 rounded-md" data-testid="customer-special-requests" />
          </div>
          <Button onClick={openRevision} disabled={submitting || cart.length === 0} className="w-full mt-3 bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="customer-submit-btn">
            Rivedi e invia — {fm(grandTotal)}
          </Button>
        </Card>
      </div>

      {/* Product detail */}
      <Dialog open={!!productDetail} onOpenChange={() => setProductDetail(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="customer-product-detail">
          {productDetail && (
            <>
              <DialogHeader><DialogTitle>{productDetail.description}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  {productDetail.image_path ? (
                    <img src={productDetail.image_path.startsWith('http') ? productDetail.image_path : `${BACKEND_URL}/api/files/${productDetail.image_path}`} alt={productDetail.description} className="w-full h-64 object-cover rounded-lg border border-stone-200" />
                  ) : (
                    <div className="w-full h-64 bg-stone-100 rounded-lg flex items-center justify-center"><ImageIcon className="w-16 h-16 text-stone-300" /></div>
                  )}
                </div>
                <div className="space-y-3">
                  <div><p className="text-xs text-stone-500">Codice</p><p className="font-mono font-semibold">{productDetail.code}</p></div>
                  <div><p className="text-xs text-stone-500">{t.pCust_filterCategory}</p><p className="text-sm">{productDetail.category_name}</p></div>
                  {productDetail.note && <div><p className="text-xs text-stone-500">Note</p><p className="text-sm">{productDetail.note}</p></div>}
                  <div><p className="text-xs text-stone-500">Prezzo</p><p className="text-3xl font-bold text-[#8A1C2A]">{productDetail.price > 0 ? `${fm(productDetail.price)}` : "—"}<span className="text-sm font-normal text-stone-500"> / {productDetail.unit || "Kg"}</span></p></div>
                  <Button onClick={() => { addToCart(productDetail); setProductDetail(null); }} disabled={!productDetail.price} className="w-full bg-[#8A1C2A] hover:bg-[#6B1420] text-white mt-2">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Aggiungi al carrello
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revision step */}
      <Dialog open={showRevision} onOpenChange={(o) => !o && setShowRevision(false)}>
        <DialogContent className="max-w-3xl bg-white max-h-[95vh] overflow-y-auto" data-testid="customer-revision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#8A1C2A]" /> Conferma riepilogo ordine</DialogTitle>
          </DialogHeader>
          {customer && (
            <div className="space-y-3">
              <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
                <p className="font-semibold">{customer.business_name}</p>
                <p className="text-xs text-stone-600">P.IVA {customer.vat_number} · {customer.email}</p>
                {(() => {
                  const da = resolveDeliveryAddress();
                  return da ? (
                    <div className="mt-2 pt-2 border-t border-stone-200">
                      <p className="text-xs font-semibold text-stone-700">📍 Indirizzo consegna {da.label && <span className="text-[#8A1C2A]">({da.label})</span>}</p>
                      <p className="text-xs">{da.address}, {da.city} {da.cap} {da.province && `(${da.province})`}</p>
                      {da.notes && <p className="text-xs italic text-stone-600">Note: {da.notes}</p>}
                      {deliveryNotes && <p className="text-xs italic text-amber-700">📝 Note ordine: {deliveryNotes}</p>}
                    </div>
                  ) : null;
                })()}
              </Card>
              <Card className="bg-white border-stone-200 overflow-hidden">
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead className="bg-stone-100">
                    <tr><th className="text-left p-2 text-xs">Codice</th><th className="text-left p-2 text-xs">Descrizione</th><th className="text-right p-2 text-xs">Q.tà</th><th className="text-right p-2 text-xs">€/UM</th><th className="text-right p-2 text-xs">Totale</th></tr>
                  </thead>
                  <tbody>
                    {cart.map((it, idx) => (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="p-2 font-mono text-xs">{it.code}</td>
                        <td className="p-2">{it.description}</td>
                        <td className="p-2 text-right">{it.quantity} {it.unit}</td>
                        <td className="p-2 text-right">{fm(it.unit_price)}</td>
                        <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200 bg-stone-50"><td colSpan="4" className="p-2 text-right">Imponibile</td><td className="p-2 text-right font-semibold">{fm(subtotal)}</td></tr>
                    <tr><td colSpan="4" className="p-2 text-right text-xs">IVA {isForeign ? "(0%)" : ""}</td><td className="p-2 text-right text-xs">{fm(vatTotal)}</td></tr>
                    <tr className="border-t border-stone-200 bg-amber-50"><td colSpan="4" className="p-2 text-right font-bold">Totale</td><td className="p-2 text-right text-lg font-bold text-[#8A1C2A]">{fm(grandTotal)}</td></tr>
                  </tfoot>
                </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100" data-testid="cust-revision-items-mobile">
                  {cart.map((it, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-mono text-stone-500">{it.code}</p>
                          <p className="text-sm font-medium leading-tight">{it.description}</p>
                        </div>
                        <p className="font-semibold text-[#8A1C2A] text-sm whitespace-nowrap">{fm(it.line_total)}</p>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-stone-600">
                        <span>{it.quantity} {it.unit}</span>
                        <span>{fm(it.unit_price)} / {it.unit || "Kg"}</span>
                      </div>
                    </div>
                  ))}
                  <div className="p-3 bg-stone-50 text-sm space-y-1">
                    <div className="flex justify-between"><span>Imponibile</span><span className="font-semibold">{fm(subtotal)}</span></div>
                    <div className="flex justify-between text-xs"><span>IVA {isForeign ? "(0%)" : ""}</span><span>{fm(vatTotal)}</span></div>
                    <div className="flex justify-between border-t border-stone-200 pt-1 mt-1 bg-amber-50 -mx-3 px-3 py-1"><span className="font-bold">Totale</span><span className="font-bold text-lg text-[#8A1C2A]">{fm(grandTotal)}</span></div>
                  </div>
                </div>
              </Card>
              {specialRequests && (
                <Card className="bg-yellow-50 border-yellow-200 p-3 text-sm">
                  <p className="text-xs font-semibold text-yellow-900 mb-1">{t.pAgent_specialRequests}</p>
                  <p className="text-xs whitespace-pre-wrap">{specialRequests}</p>
                </Card>
              )}
              <DialogFooter className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowRevision(false)} className="border-stone-300" data-testid="customer-revision-back">← Torna indietro</Button>
                <Button onClick={submit} disabled={submitting} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="customer-revision-confirm">
                  <Check className="w-4 h-4 mr-1" /> {submitting ? "Invio..." : "Conferma e invia all'agente"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CustomerOffers = () => {
  const fm = useFmt();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/offers`)
      .then(r => setOffers(r.data || []))
      .catch(() => toast.error("Errore caricamento offerte"))
      .finally(() => setLoading(false));
    axios.post(`${API}/offers/mark-seen`).catch(() => {});
  }, []);

  const previewSingle = (id) => openAuthenticatedPdf(`/offers/${id}/pdf`);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-semibold text-stone-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-[#C5A059]" /> Offerte Speciali
        </h1>
        <p className="text-stone-500 text-sm mt-1">Offerte attive del nostro magazzino — clicca "Ordina" per crearne un ordine</p>
      </div>
      {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : offers.length === 0 ? (
          <Card className="bg-white border-stone-200 p-12 text-center">
            <Sparkles className="w-14 h-14 mx-auto text-stone-300" />
            <p className="text-stone-500 mt-3">Nessuna offerta attiva al momento</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map(o => (
              <Card key={o.id} className="bg-white border-[#C5A059] p-3 overflow-hidden hover:shadow-md transition-shadow" data-testid={`customer-offer-${o.id}`}>
                {o.images?.[0] && <img src={o.images[0].startsWith("http") ? o.images[0] : `${BACKEND_URL}/api/files/${o.images[0]}`} alt={o.title} className="w-full h-44 object-cover rounded mb-3 border border-stone-200" />}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-stone-900 leading-tight flex-1">{o.title}</h3>
                </div>
                {o.category_name && <span className="inline-block text-[10px] text-[#8A1C2A] bg-[#8A1C2A]/10 px-2 py-0.5 rounded font-medium mb-1">{o.category_name}</span>}
                <p className="text-3xl font-bold text-[#8A1C2A] mt-1">{fm(o.price)} <span className="text-xs font-normal text-stone-500">/ {o.unit}</span></p>
                {o.description && <p className="text-xs text-stone-600 mt-1">{o.description}</p>}
                {o.notes && <p className="text-xs italic text-stone-500 mt-1">{o.notes}</p>}
                <p className="text-xs text-stone-500 mt-1">Valida fino al {formatItalianDate(o.expires_at)}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => previewSingle(o.id)} className="text-xs border-stone-300" data-testid={`customer-offer-pdf-${o.id}`}><Eye className="w-3 h-3 mr-1" /> PDF</Button>
                  <Button size="sm" onClick={() => navigate(`/customer/new-order?offer_id=${o.id}`)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white text-xs flex-1" data-testid={`customer-order-offer-${o.id}`}>
                    <ShoppingCart className="w-3 h-3 mr-1" /> Ordina
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
};

const CustomerDedicatedOffers = () => {
  const fm = useFmt();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderOffer, setOrderOffer] = useState(null);
  const [qtyMap, setQtyMap] = useState({});
  const [unitMap, setUnitMap] = useState({});
  const [includeMap, setIncludeMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    axios.get(`${API}/dedicated-offers`)
      .then(r => setOffers(r.data || []))
      .catch(() => toast.error("Errore caricamento offerte dedicate"))
      .finally(() => setLoading(false));
  }, []);

  const openOrder = (offer) => {
    setOrderOffer(offer);
    setQtyMap({});
    setUnitMap({});
    setIncludeMap({});
    setNotes("");
  };

  const itemKey = (it, idx) => it.product_id || `free-${idx}`;

  // includeMap: true means "this line is in the cart"; qty/unit are only used when included.
  const isIncluded = (k) => !!includeMap[k];
  const getQty = (k) => Number(qtyMap[k]) || 0;
  const getUnit = (it, k) => unitMap[k] || it.unit || "Kg";

  const toggleInclude = (it, k) => {
    const next = !includeMap[k];
    setIncludeMap(prev => ({ ...prev, [k]: next }));
    if (next && !qtyMap[k]) setQtyMap(prev => ({ ...prev, [k]: 1 }));
    if (next && !unitMap[k]) setUnitMap(prev => ({ ...prev, [k]: it.unit || "Kg" }));
  };

  const totalPreview = () => {
    if (!orderOffer) return 0;
    return (orderOffer.items || []).reduce((s, it, idx) => {
      const k = itemKey(it, idx);
      if (!isIncluded(k)) return s;
      const q = getQty(k);
      return s + q * (Number(it.final_price) || 0);
    }, 0);
  };

  const submitOrder = async () => {
    if (!orderOffer) return;
    const items = (orderOffer.items || [])
      .map((it, idx) => {
        const k = itemKey(it, idx);
        if (!isIncluded(k)) return null;
        return {
          product_id: it.product_id || null,
          offer_line_index: it.product_id ? null : idx,
          quantity: getQty(k),
          unit: getUnit(it, k),
        };
      })
      .filter(it => it && it.quantity > 0);
    if (items.length === 0) {
      toast.error("Inserisci almeno una quantità");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/dedicated-offers/${orderOffer.id}/order`, { items, notes });
      toast.success(`Ordine ${r.data?.order_number || ""} inviato in approvazione`);
      setOrderOffer(null);
      navigate("/customer/orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio ordine");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="customer-dedicated-offers-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-semibold text-stone-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-[#8A1C2A]" /> Le tue offerte dedicate
        </h1>
        <p className="text-stone-500 text-sm mt-1">Offerte personalizzate riservate alla tua azienda. Ordina direttamente con i prezzi concordati.</p>
      </div>
      {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : offers.length === 0 ? (
          <Card className="bg-white border-stone-200 p-12 text-center">
            <FileText className="w-14 h-14 mx-auto text-stone-300" />
            <p className="text-stone-500 mt-3">Nessuna offerta dedicata disponibile</p>
            <p className="text-xs text-stone-400 mt-1">Chiedi al tuo agente di crearne una</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {offers.map(o => (
              <Card key={o.id} className="bg-white border-stone-200 overflow-hidden hover:shadow-md transition" data-testid={`customer-dedoff-${o.id}`}>
                <div className="h-1 bg-gradient-to-r from-[#8A1C2A] to-[#C5A059]" />
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#C5A059] font-semibold">Offerta dedicata</p>
                    <p className="text-lg font-semibold text-stone-900">{o.items?.length || 0} articoli a prezzo riservato</p>
                    <p className="text-xs text-stone-500">
                      Creata il {new Date(o.created_at).toLocaleDateString("it-IT")} {o.notes ? `· ${o.notes}` : ""}
                    </p>
                  </div>
                  <div className="border border-stone-100 rounded max-h-44 overflow-y-auto divide-y divide-stone-50">
                    {(o.items || []).slice(0, 4).map((it, idx) => (
                      <div key={it.product_id || `free-${idx}`} className="px-3 py-1.5 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{it.description}</p>
                          <p className="text-[10px] text-stone-500 font-mono">{it.code || "—"}{it.is_free ? " · LIBERO" : ""}</p>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <p className="text-sm font-bold text-[#8A1C2A]">{fm(it.final_price)}</p>
                          {(it.discount_pct || 0) > 0 && <p className="text-[10px] text-stone-400 line-through">{fm(it.base_price)}</p>}
                        </div>
                      </div>
                    ))}
                    {(o.items || []).length > 4 && <div className="px-3 py-1.5 text-[11px] text-stone-500 text-center">+ {(o.items || []).length - 4} altri articoli</div>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => openAuthenticatedPdf(`/dedicated-offers/${o.id}/pdf`)} className="flex-1" data-testid={`customer-dedoff-pdf-${o.id}`}>
                      <Eye className="w-4 h-4 mr-1" />PDF
                    </Button>
                    <Button onClick={() => openOrder(o)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white flex-1" data-testid={`customer-dedoff-order-${o.id}`}>
                      <ShoppingCart className="w-4 h-4 mr-1" />Ordina
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Order from dedicated offer dialog */}
      <Dialog open={!!orderOffer} onOpenChange={(v) => !v && setOrderOffer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="customer-dedoff-order-dialog">
          {orderOffer && (
            <>
              <DialogHeader>
                <DialogTitle>Ordina dalla tua offerta dedicata</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-1 space-y-3">
                <p className="text-xs text-stone-500">Seleziona solo gli articoli che vuoi ordinare e indica quantità + unità di misura. I prezzi sono quelli concordati nell&apos;offerta.</p>
                <div className="border border-stone-200 rounded-lg overflow-hidden divide-y divide-stone-100">
                  {(orderOffer.items || []).map((it, idx) => {
                    const k = itemKey(it, idx);
                    const included = isIncluded(k);
                    const q = included ? getQty(k) : 0;
                    const total = q * (Number(it.final_price) || 0);
                    return (
                      <div key={k} className={`p-3 grid grid-cols-12 gap-2 items-center transition ${included ? "bg-white" : "bg-stone-50/60"}`} data-testid={`customer-dedoff-item-${k}`}>
                        <div className="col-span-12 sm:col-span-1 flex items-center justify-center">
                          <Checkbox
                            checked={included}
                            onCheckedChange={() => toggleInclude(it, k)}
                            data-testid={`customer-dedoff-include-${k}`}
                          />
                        </div>
                        <div className="col-span-12 sm:col-span-5">
                          <div className="flex items-center gap-1.5">
                            <p className={`text-sm font-medium ${included ? "text-stone-900" : "text-stone-400"}`}>{it.description}</p>
                            {it.is_free && <Badge variant="outline" className="text-[9px] py-0 px-1 border-amber-400 text-amber-700">LIBERO</Badge>}
                          </div>
                          <p className={`text-[11px] font-mono ${included ? "text-stone-500" : "text-stone-300"}`}>{it.code || "—"}</p>
                          <p className={`text-xs font-bold mt-0.5 ${included ? "text-[#8A1C2A]" : "text-stone-400"}`}>{fm(it.final_price)} / {it.unit || "Kg"}</p>
                        </div>
                        <div className="col-span-5 sm:col-span-2">
                          <Label className="text-[10px] text-stone-500 uppercase">Quantità</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={qtyMap[k] ?? ""}
                            onChange={(e) => setQtyMap(prev => ({ ...prev, [k]: e.target.value }))}
                            disabled={!included}
                            placeholder="0"
                            className="h-8 text-sm"
                            data-testid={`customer-dedoff-qty-${k}`}
                          />
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="text-[10px] text-stone-500 uppercase">U.M.</Label>
                          <Select value={getUnit(it, k)} onValueChange={(v) => setUnitMap(prev => ({ ...prev, [k]: v }))} disabled={!included}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`customer-dedoff-unit-${k}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Kg">Kg</SelectItem>
                              <SelectItem value="Pz">Pz</SelectItem>
                              <SelectItem value="CT">CT</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3 sm:col-span-2 text-right">
                          <Label className="text-[10px] text-stone-500 uppercase">Totale</Label>
                          <p className={`text-sm font-semibold ${included ? "text-stone-700" : "text-stone-400"}`}>{fm(total)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <Label className="text-sm">Note per l&apos;ordine</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note di consegna..." data-testid="customer-dedoff-notes" />
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded p-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-700">Totale stimato</span>
                  <span className="text-xl font-bold text-[#8A1C2A]" data-testid="customer-dedoff-total">{fm(totalPreview())}</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOrderOffer(null)}>Annulla</Button>
                <Button onClick={submitOrder} disabled={submitting} className="bg-[#8A1C2A] hover:bg-[#6B1420]" data-testid="customer-dedoff-submit">
                  {submitting ? "Invio..." : "Invia ordine"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CustomerPortal = ({ user, logout }) => {
  const [chatOpen, setChatOpen] = useState(false);
  return (
    <div className="min-h-screen bg-stone-50">
      <Header user={user} logout={logout} onChatClick={() => setChatOpen(true)} />
      <Routes>
        <Route path="/orders" element={<OrdersHistory />} />
        <Route path="/documents" element={<div className="max-w-6xl mx-auto p-4"><DocumentsPage currentUser={user} company={null} /></div>} />
        <Route path="/statements" element={<div className="max-w-6xl mx-auto p-4"><StatementsPage currentUser={user} company={null} /></div>} />
        <Route path="/offers" element={<CustomerOffers />} />
        <Route path="/dedicated-offers" element={<CustomerDedicatedOffers />} />
        <Route path="/new-order" element={<CustomerNewOrder />} />
        <Route path="/tutorials" element={<div className="max-w-6xl mx-auto p-4"><TutorialsPage currentUser={user} /></div>} />
        <Route path="*" element={<Navigate to="/customer/orders" />} />
      </Routes>
      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
};

export default CustomerPortal;
